// ui/GameOverOverlay.jsx: M2.c-10e end-of-game overlay.
//
// Simple DOM overlay (native <dialog>) that opens when state.game_over
// is true. Shows the winning team + final score + a Play Again button
// that resets the engine and starts a new game.
//
// Why <dialog>: same precedent as EndOfHandModal, native focus trap +
// Escape-to-dismiss, accessible by default, no library dependency. We
// disable Escape here (the user must explicitly click Play Again or
// close the tab) by preventing the default cancel event, a finished
// game shouldn't silently dismiss.

import { useEffect, useRef, useState } from 'preact/hooks';
import { TEAM_LABEL, deriveWinner, formatScoreLine, formatViewerResult, gameOverHeadline, GAME_OVER_NEXT_GAME_TEXT } from './game-over.js';
import { gameOverCardPosition } from './game-over-position.js';
import { ReadyCountdown } from './ReadyCountdown.jsx';
import { ScoreBlock } from './CreamTable.jsx';
// The sheets' sheets, in the order the game view loads them (later wins an
// equal-specificity tie): this file can be reached before the view's own imports.
import '../styles/room.css';
import '../styles/networked-game.css';
import '../styles/fonts.css';
import '../styles/cream-table.css';
import '../styles/cream-game-panels.css';

/**
 * @param {{
 *   open: boolean,
 *   summary: { delta_team?: string, game_over?: boolean }|null|undefined,
 *   scores: { team_a: number, team_b: number }|null|undefined,
 *   winningTeam?: 'team_a'|'team_b'|null,
 *   onPlayAgain: () => void,
 *   viewerTeam?: 'team_a'|'team_b'|null,
 *   onAcknowledge?: (() => void)|null,
 *   acknowledgeLabel?: string|null,
 *   deadlineMs?: number|null,
 *   clockOffsetMs?: number,
 *   waiting?: boolean,
 *   waitingText?: string|null,
 *   tableLayout?: import('../canvas/layout.js').TableLayout|null,
 *   tableState?: any,
 *   stageRef?: { current: HTMLElement|null }|null,
 *   target?: number,
 *   tableMode?: string,
 * }} props
 */
export function GameOverOverlay({
  open, summary, scores, winningTeam, onPlayAgain,
  viewerTeam = null, onAcknowledge = null, acknowledgeLabel = null,
  deadlineMs = null, clockOffsetMs = 0,
  waiting = false, waitingText = null,
  tableLayout = null, tableState = null, stageRef = null,
  target = 10, tableMode = 'desk',
}) {
  /** @type {{ current: HTMLDialogElement|null }} */
  const dialogRef = useRef(null);
  /** @type {{ current: HTMLButtonElement|null }} */
  const buttonRef = useRef(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      // Focus Play Again on open so keyboard users land on the
      // primary action.
      requestAnimationFrame(() => buttonRef.current?.focus());
    } else if (!open && d.open) {
      d.close();
    }
  }, [open]);

  // B4b-1 U7: on short landscape tables (844x390, 667x375) the centred
  // card covered the partner's plate and the trump label. There the card
  // takes two columns and moves to the free spot nearest the table's
  // centre (game-over-position.js); elsewhere it stays centred.
  const compactCard = !!tableLayout?.compact;
  // Restyle step 2: the cream table names the widest the card may be to
  // keep off its seat tags (landscape), and the card then stacks.
  const narrowW = /** @type {any} */ (tableLayout)?.cardMaxWidth ?? null;
  const [place, setPlace] = useState(/** @type {{left:number, top:number}|null} */ (null));
  useEffect(() => {
    const d = dialogRef.current;
    const stage = stageRef?.current;
    if (!open || !compactCard || !d || !d.open || !stage || !tableLayout) {
      setPlace(null);
      return;
    }
    const card = d.getBoundingClientRect();
    const table = stage.getBoundingClientRect();
    setPlace(gameOverCardPosition(tableLayout, tableState,
      { x: table.x, y: table.y }, { w: window.innerWidth, h: window.innerHeight },
      { w: Math.ceil(card.width), h: Math.ceil(card.height) }));
  }, [open, compactCard, tableLayout, tableState, stageRef]);

  // Block Escape, game-over isn't dismissable, it requires an action.
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    const onCancel = (e) => e.preventDefault();
    d.addEventListener('cancel', onCancel);
    return () => d.removeEventListener('cancel', onCancel);
  }, []);

  const winner = deriveWinner({ winningTeam, summary, scores });
  const winnerLabel = winner ? TEAM_LABEL[winner] : '—';
  const scoreLine = formatScoreLine(scores);
  // B2-8: networked mode reads the result from the viewer's side and
  // acknowledges the final recap instead of starting a new game here.
  const networked = onAcknowledge != null;
  const viewerLine = viewerTeam && winner
    ? formatViewerResult(scores, viewerTeam, winner)
    : '';
  const action = networked ? onAcknowledge : onPlayAgain;
  // W8: after Back to lobby is clicked the button waits like the recap's
  // Continue — disabled and reading "Waiting for the others…" until the
  // lobby arrives.
  const actionLabel = waiting
    ? (waitingText || 'Waiting for the others…')
    : (networked ? (acknowledgeLabel || 'Back to lobby') : 'Play Again');
  const actionTestId = networked ? 'back-to-lobby' : 'play-again';

  const winnerTeam = winner;
  const headline = gameOverHeadline(winner, viewerTeam);
  const usTeam = viewerTeam ?? 'team_a';
  const themTeam = usTeam === 'team_a' ? 'team_b' : 'team_a';
  const scoreFor = (team) => scores?.[team] ?? 0;
  const body = (
    <>
      <div>
        <p class="cs-eyebrow">GAME OVER</p>
        <h2 id="game-over-title" class="cs-title">{headline}</h2>
        {scores && (
          <div class="cs-scores" data-testid="game-over-scores">
            <div class="cs-team team-US"><ScoreBlock sheet label="US" score={scoreFor(usTeam)} target={target} tricks={null} mode={tableMode} /></div>
            <div class="cs-team team-THEM"><ScoreBlock sheet label="THEM" score={scoreFor(themTeam)} target={target} tricks={null} mode={tableMode} /></div>
          </div>
        )}
        <p data-testid="game-over-summary" class="cs-over-line">
          {viewerLine || (<><strong>{winnerLabel}</strong> won {scoreLine}.</>)}
        </p>
        {/* B4a-F6: teams and settings carry over; the host starts the next
            game (Ready stays optional). Networked card only. */}
        {networked && (
          <p data-testid="game-over-next-game" class="cs-over-line">{GAME_OVER_NEXT_GAME_TEXT}</p>
        )}
      </div>
      <div class="cs-actions">
        {networked && deadlineMs != null && (
          <ReadyCountdown deadlineMs={deadlineMs} clockOffsetMs={clockOffsetMs} prefix="Back to lobby in" />
        )}
        <button
          ref={buttonRef}
          type="button"
          class="btn btn-primary"
          data-action={actionTestId}
          onClick={action}
          disabled={waiting}
          aria-disabled={waiting}
        >
          {actionLabel}
        </button>
      </div>
    </>
  );

  return (
    <dialog
      ref={dialogRef}
      class="cream-sheet game-over-sheet"
      data-testid="game-over-overlay"
      aria-labelledby="game-over-title"
      data-placement={place ? 'beside-table' : 'centre'}
      data-compact={compactCard ? 'true' : 'false'}
      data-winner={winnerTeam ?? undefined}
      style={{
        // The narrow card (landscape, beside the trick block) is as wide as
        // the free space the table names.
        ...(narrowW ? { maxWidth: narrowW } : {}),
        ...(place ? { margin: 0, left: place.left, top: place.top, right: 'auto', bottom: 'auto' } : {}),
      }}
    >
      <div class="cs-body">{body}</div>
    </dialog>
  );
}
